import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, onValue, push, set } from 'firebase/database';
import { database } from '../../firebase';
import axios from 'axios';
import ManagerEmployeeDrawer from './EmployeeDrawer/ManagerEmployeeDrawer';

const ManagerHub = () => {
  const { employeeContext, currentUser } = useAuth();
  const { companyId, employeeId } = employeeContext || {};
  
  const [team, setTeam] = useState([]);
  const [employeesRaw, setEmployeesRaw] = useState({});
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Drawer State
  const [selectedEmpId, setSelectedEmpId] = useState(null);

  useEffect(() => {
    if (!companyId || !employeeId) return;

    const employeesRef = ref(database, `companies/${companyId}/employees`);
    const unsubscribe = onValue(employeesRef, (snap) => {
      const data = snap.val() || {};
      setEmployeesRaw(data); // Store raw data for drawer
      
      const teamMembers = [];
      const requests = [];

      Object.entries(data).forEach(([id, emp]) => {
        const empManagerId = emp.managerId || emp.profile?.managerUID;
        
        if (empManagerId === employeeId) {
          teamMembers.push({ id, ...emp });

          if (emp.pto && emp.pto.requests) {
            Object.values(emp.pto.requests).forEach(req => {
              if (req.status === 'pending') {
                requests.push({
                  ...req,
                  employeeName: emp.profile?.name || emp.name || 'Unknown',
                  employeeId: id
                });
              }
            });
          }
        }
      });

      setTeam(teamMembers);
      setPendingRequests(requests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId, employeeId]);

  const handleApproval = async (req, status) => {
    setProcessing(req.id);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";
      await axios.post(`${backendUrl}/employee/approve-pto`, {
        companyUID: companyId,
        employeeId: req.employeeId,
        requestId: req.id,
        action: status === 'approved' ? 'approve' : 'reject',
        approvedByUID: employeeId,
        approvedByName: currentUser?.displayName || 'Manager'
      });

      // Send Notification (Firebase)
      const notifRef = ref(database, `companies/${companyId}/employees/${req.employeeId}/notifications`);
      const newNotifRef = push(notifRef);
      const title = `PTO Request ${status === 'approved' ? 'Approved' : 'Rejected'}`;
      const message = `Your PTO request for ${req.startDate} to ${req.endDate} has been ${status}.`;
      
      await set(newNotifRef, {
        title,
        message,
        type: status === 'approved' ? 'success' : 'error',
        timestamp: Date.now(),
        read: false
      });

      // Send Notification (Email)
      try {
          await axios.post(`${backendUrl}/send-notification-email`, {
              companyId,
              employeeId: req.employeeId,
              subject: title,
              message: message,
              type: status === 'approved' ? 'success' : 'error'
          });
      } catch (e) {
          console.warn('Email notification failed', e);
      }

      alert(`Request ${status} successfully.`);
    } catch (error) {
      console.error('Error processing approval:', error);
      alert('Failed to process request.');
    }
    setProcessing(null);
  };

  if (loading) return <div>Loading team data...</div>;

  return (
    <div className="page-container">
      <h1>Manager Hub</h1>

      {/* Pending Approvals */}
      <div style={{ marginTop: '30px', marginBottom: '40px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          Pending Approvals
          {pendingRequests.length > 0 && (
            <span style={{ background: '#EBDD40', color: '#000', fontSize: '12px', padding: '2px 8px', borderRadius: '10px' }}>
              {pendingRequests.length}
            </span>
          )}
        </h2>
        
        {pendingRequests.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.5)' }}>
            <i className="fa-solid fa-check-circle" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}></i>
            All caught up! No pending requests.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {pendingRequests.map(req => (
              <div key={req.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '16px', marginBottom: '5px' }}>{req.employeeName}</h3>
                    <div style={{ color: '#568ded', fontWeight: 'bold', textTransform: 'capitalize' }}>
                      {req.type} Request
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                    {req.days} Days
                  </div>
                </div>
                
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                    <span><i className="fa-solid fa-calendar-plus"></i> From: {req.startDate}</span>
                    <span><i className="fa-solid fa-calendar-minus"></i> To: {req.endDate}</span>
                  </div>
                  {req.comment && <div style={{ fontStyle: 'italic', marginTop: '5px' }}>"{req.comment}"</div>}
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button 
                    className="btn-primary" 
                    style={{ background: '#EB4040', opacity: processing === req.id ? 0.5 : 1 }}
                    onClick={() => handleApproval(req, 'rejected')}
                    disabled={processing === req.id}
                  >
                    Reject
                  </button>
                  <button 
                    className="btn-primary"
                    style={{ background: '#40EB6E', opacity: processing === req.id ? 0.5 : 1 }}
                    onClick={() => handleApproval(req, 'approved')}
                    disabled={processing === req.id}
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team Roster */}
      <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>My Team ({team.length})</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
        {team.map(member => (
          <div 
            key={member.id} 
            className="card"
            // Make card clickable
            onClick={() => setSelectedEmpId(member.id)}
            style={{ cursor: 'pointer', transition: 'transform 0.2s', border: '1px solid transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#568ded'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
              <div style={{ 
                width: '40px', height: '40px', borderRadius: '50%', 
                background: '#568ded', display: 'flex', 
                alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
              }}>
                {(member.profile?.name || member.name || 'U').charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: 'bold' }}>{member.profile?.name || member.name}</div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{member.profile?.role || member.role}</div>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
              <div><i className="fa-solid fa-envelope"></i> {member.contact?.email || member.email || 'No email'}</div>
              <div style={{ marginTop: '5px' }}>
                <i className="fa-solid fa-umbrella-beach"></i> Balance: {member.pto?.balanceDays || 0} days
              </div>
            </div>
            <div style={{ marginTop: '10px', textAlign: 'right', fontSize: '12px', color: '#568ded' }}>
                View Details <i className="fa-solid fa-arrow-right"></i>
            </div>
          </div>
        ))}
      </div>

      {/* Slide-out Drawer */}
      {selectedEmpId && (
        <ManagerEmployeeDrawer
          employeeId={selectedEmpId}
          employeesRaw={employeesRaw}
          drawerOpen={!!selectedEmpId}
          onClose={() => setSelectedEmpId(null)}
        />
      )}
    </div>
  );
};

export default ManagerHub;
